import { FunctionComponent, useState } from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import { ConditionLink, PriceCondition } from '@/models/conditionLink';
import { Drawer, Stack, Typography } from '@mui/material';
import { ConditionPreviewList } from './ConditionPreviewList';
import {
    useGetConditionLinkQuery,
    usePostOrPutConditionLinkMutation,
} from '@/api/v2/conditionLinks';
import { PriceConditionFormContainer } from '@/pages/rulesets/components/conditions/PriceConditionFormContainer';
import { ConfirmationDialog } from '@/pages/rulesets/components/ConfirmationDialog';

type PriceConditionConfigurationProps = {
    conditionLink: ConditionLink;
    onUpdateConditionLink: (conditionLink: ConditionLink) => void;
};

export const PriceConditionConfiguration: FunctionComponent<
    PriceConditionConfigurationProps
> = ({ conditionLink, onUpdateConditionLink }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { isLoading: getQueryLoading } = useGetConditionLinkQuery({
        liftOperatorId,
        id: conditionLink.id,
    });
    const [apiAction, { isLoading: updateLoading }] =
        usePostOrPutConditionLinkMutation({
            liftOperatorId,
            conditionLink,
        });

    const isLoading = getQueryLoading || updateLoading;
    const { addNotification } = useNotification();
    const [conditionToDelete, setConditionToDelete] =
        useState<PriceCondition | null>(null);

    const [conditionDialogConfig, setConditionDialogConfig] = useState<{
        isOpen: boolean;
        conditionTemplate: PriceCondition;
        options: {
            insertAtIndex?: number;
        } | null;
    }>({
        isOpen: false,
        conditionTemplate: null,
        options: null,
    });

    const bulkUpdateConditions = (newConditionsSorted: PriceCondition[]) => {
        apiAction({
            liftOperatorId,
            conditionLink: {
                ...conditionLink,
                priceConditions: newConditionsSorted.map((c, index) => ({
                    ...c,
                    sort: index,
                })),
            },
        })
            .unwrap()
            .then((conditionLink) => {
                addNotification({
                    message: 'Sortierung der Regel aktualisiert',
                    type: 'success',
                });

                onUpdateConditionLink(conditionLink);
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern der neuen Sortierung. Bitte versuchen Sie es später noch einmal',
                    type: 'error',
                });
            });
    };

    const updateConditionSort = (
        conditions: PriceCondition[],
        condition: PriceCondition,
        priorityChange: 'increase' | 'decrease'
    ) => {
        const cToUpdateIndex = conditions.findIndex(
            (c) => c.id === condition.id
        );

        if (cToUpdateIndex > -1) {
            const cToReplacedIndex =
                priorityChange === 'increase'
                    ? cToUpdateIndex - 1
                    : cToUpdateIndex + 1;

            if (conditions[cToReplacedIndex]) {
                const conditionToUpdate = { ...conditions[cToUpdateIndex] };
                const conditionToReplace = { ...conditions[cToReplacedIndex] };
                const toReplaceSort = conditionToReplace.sort;
                const toUpdateSort = conditionToUpdate.sort;

                conditions.splice(cToUpdateIndex, 1, {
                    ...conditionToUpdate,
                    sort: toReplaceSort,
                });

                conditions.splice(cToReplacedIndex, 1, {
                    ...conditionToReplace,
                    sort: toUpdateSort,
                });

                apiAction({
                    liftOperatorId,
                    conditionLink: {
                        ...conditionLink,
                        priceConditions: [...conditions],
                    },
                })
                    .unwrap()
                    .then((conditionLink) => {
                        addNotification({
                            message: 'Sortierung der Regel aktualisiert',
                            type: 'success',
                        });
                        onUpdateConditionLink(conditionLink);
                    })
                    .catch(() => {
                        addNotification({
                            message:
                                'Fehler beim Speichern der neuen Sortierung. Bitte versuchen Sie es später noch einmal',
                            type: 'error',
                        });
                    });
            }
        }
    };

    const deleteCondition = (condition: PriceCondition) => {
        const conditions = [...conditionLink.priceConditions];
        const conditionIndex = conditions.findIndex(
            (c) => c.id === condition.id
        );

        if (conditionIndex > -1) {
            conditions.splice(conditionIndex, 1);

            apiAction({
                liftOperatorId,
                conditionLink: {
                    ...conditionLink,
                    priceConditions: [...conditions],
                },
            })
                .unwrap()
                .then((conditionLink) => {
                    addNotification({
                        message: 'Regel gelöscht',
                        type: 'success',
                    });

                    onUpdateConditionLink(conditionLink);
                    setConditionToDelete(null);
                })
                .catch(() => {
                    addNotification({
                        message: 'Fehler beim Löschen der Regel',
                        type: 'error',
                    });
                });
        }
    };

    return (
        <Stack spacing={3}>
            <Typography gutterBottom>
                Sie können mehrere Verfügbarkeits-Regeln hinterlegen. <br />
                Die Regeln kommen der Priorität nach zur Anwendung.
            </Typography>

            <Typography>
                Bitte beachten: Ein in der „allgemeinen Konfiguration“
                hinterlegtes Kontingent braucht sich durch diese Regeln
                ebenfalls auf.
            </Typography>

            <ConditionPreviewList
                conditions={conditionLink.priceConditions}
                onCreateOrUpdate={(condition: PriceCondition, options) => {
                    setConditionDialogConfig({
                        isOpen: true,
                        conditionTemplate: condition || null,
                        options: options
                            ? {
                                  insertAtIndex: options?.insertAtIndex,
                              }
                            : null,
                    });
                }}
                onDelete={(condition: PriceCondition) => {
                    setConditionToDelete(condition);
                }}
                onBulkUpdateConditions={bulkUpdateConditions}
                onPriorityChange={updateConditionSort}
                isLoading={isLoading}
                onAddClick={(options) => {
                    setConditionDialogConfig({
                        isOpen: true,
                        conditionTemplate: null,
                        options: options
                            ? {
                                  insertAtIndex: options?.insertAtIndex,
                              }
                            : null,
                    });
                }}
            />

            <Drawer
                onClose={() => {
                    setConditionDialogConfig({
                        isOpen: false,
                        conditionTemplate: null,
                        options: null,
                    });
                }}
                anchor='right'
                open={conditionDialogConfig.isOpen}
            >
                <PriceConditionFormContainer
                    onClose={() => {
                        setConditionDialogConfig((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    condition={conditionDialogConfig.conditionTemplate}
                    conditionLink={conditionLink}
                    onUpdateConditionLink={onUpdateConditionLink}
                />
            </Drawer>

            <ConfirmationDialog
                open={!!conditionToDelete}
                body='Wollen Sie die Regel wirklich löschen'
                confirmButtonText='Regel löschen'
                onClose={() => {
                    setConditionToDelete(null);
                }}
                onConfirm={() => {
                    deleteCondition(conditionToDelete);
                }}
                deleteLoading={isLoading}
            />
        </Stack>
    );
};
